// Issue list page
// Reference: spec.md US2 - leaseholder views their issues

import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui';
import { StatusTabs } from '../../components/issues/StatusTabs';
import { IssueListItem } from '../../components/issues/IssueListItem';
import { api } from '../../services/api';
import type { IssueWithRelations } from '@propman/shared';

type Tab = 'in_progress' | 'your_tasks' | 'completed' | 'reported';

interface IssuesResponse {
  issues: IssueWithRelations[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    hasMore: boolean;
  };
}

export function IssueListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<Tab>(
    (searchParams.get('tab') as Tab) || 'in_progress'
  );
  const [issues, setIssues] = useState<IssueWithRelations[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);

  // Check for success message from issue creation
  const createdRef = searchParams.get('created');

  useEffect(() => {
    async function loadIssues() {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get<IssuesResponse>('/issues', {
          tab: activeTab,
          page,
          pageSize: 20,
        });

        if (page === 1) {
          setIssues(response.issues);
        } else {
          setIssues((prev) => [...prev, ...response.issues]);
        }

        setHasMore(response.meta.hasMore);
        setTotal(response.meta.total);
      } catch (err) {
        console.error('Failed to load issues:', err);
        setError('Failed to load issues. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    loadIssues();
  }, [activeTab, page]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab as Tab);
    setPage(1);
    setIssues([]);
    setSearchParams({ tab });
  };

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };

  const dismissSuccessMessage = () => {
    searchParams.delete('created');
    setSearchParams(searchParams);
  };

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Issues</h1>
          <p className="mt-1 text-sm text-gray-600">
            {total > 0 ? `${total} issue${total !== 1 ? 's' : ''} found` : 'View and track your reported issues'}
          </p>
        </div>
        <Link to="/issues/new">
          <Button>
            <svg className="w-5 h-5 mr-2 -ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Report Issue
          </Button>
        </Link>
      </div>

      {/* Success message */}
      {createdRef && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm text-green-700">
              Issue <span className="font-medium">{createdRef}</span> has been created successfully.
            </p>
          </div>
          <button
            onClick={dismissSuccessMessage}
            className="text-green-500 hover:text-green-700"
            aria-label="Dismiss"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Tabs */}
      <StatusTabs activeTab={activeTab} onChange={handleTabChange} />

      {/* Content */}
      <div className="mt-6">
        {/* Error state */}
        {error && (
          <div className="text-center py-12">
            <div className="text-red-500 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-gray-600">{error}</p>
            <Button
              variant="secondary"
              className="mt-4"
              onClick={() => {
                setPage(1);
                setIssues([]);
              }}
            >
              Try Again
            </Button>
          </div>
        )}

        {/* Loading state (initial) */}
        {isLoading && issues.length === 0 && (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading issues...</p>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && issues.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              {activeTab === 'in_progress' && 'No active issues'}
              {activeTab === 'completed' && 'No completed issues'}
              {activeTab === 'your_tasks' && 'No assigned tasks'}
              {activeTab === 'reported' && 'No reported issues'}
            </h3>
            <p className="mt-2 text-gray-600">
              {activeTab === 'in_progress' && 'There are no issues currently being worked on.'}
              {activeTab === 'completed' && 'No issues have been resolved yet.'}
              {activeTab === 'your_tasks' && 'You have no tasks assigned to you.'}
              {activeTab === 'reported' && "You haven't reported any issues yet."}
            </p>
            {(activeTab === 'reported' || activeTab === 'in_progress') && (
              <Link to="/issues/new">
                <Button className="mt-4">Report an Issue</Button>
              </Link>
            )}
          </div>
        )}

        {/* Issue list */}
        {issues.length > 0 && (
          <div className="space-y-4">
            {issues.map((issue) => (
              <IssueListItem key={issue.id} issue={issue} />
            ))}
          </div>
        )}

        {/* Load more */}
        {hasMore && !isLoading && (
          <div className="mt-6 text-center">
            <Button variant="secondary" onClick={handleLoadMore}>
              Load More
            </Button>
          </div>
        )}

        {/* Loading more indicator */}
        {isLoading && issues.length > 0 && (
          <div className="mt-6 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto" />
          </div>
        )}
      </div>
    </div>
  );
}

export default IssueListPage;
